import { useEffect, useState } from 'react';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { useSession } from '../session/SessionProvider';
import {
  createMockHistory,
  fetchHistory,
  fetchSummary,
  summarizeLocally,
  type HistoryPoint,
  type HistorySummary,
  type HistoryWindow,
  type MetricKey,
} from './history';

export interface HistoryResult {
  points: HistoryPoint[];
  /** Minim/maxim/medie pe schimbările brute; `null` până la primul răspuns. */
  summary: HistorySummary | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: HistoryResult = { points: [], summary: null, loading: true, error: null };

/**
 * Cache scurt per fereastră și metrică: comutarea între pastile și întoarcerea
 * dintr-o zonă mărită revin la ferestre deja cerute, iar fără cache fiecare pas
 * înapoi ar însemna o pereche nouă de cereri.
 */
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; points: HistoryPoint[]; summary: HistorySummary }>();

function cacheKey(window: HistoryWindow, metrics: MetricKey[]): string {
  return `${window.from}|${window.to}|${window.bucket}|${metrics.join(',')}`;
}

export function useHistory(window: HistoryWindow, metrics: MetricKey[]): HistoryResult {
  const { status: sessionStatus, getToken, tokenVersion } = useSession();
  const [result, setResult] = useState<HistoryResult>(EMPTY);
  const key = cacheKey(window, metrics);

  useEffect(() => {
    if (getRuntimeConfig().demo) {
      const points = createMockHistory(new Date(), window);
      setResult({ points, summary: summarizeLocally(points), loading: false, error: null });
      return;
    }

    const cached = cache.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setResult({ points: cached.points, summary: cached.summary, loading: false, error: null });
      return;
    }

    // Fără bilet valid nu are rost să cerem: așteptăm sesiunea.
    if (sessionStatus !== 'ready') {
      setResult({ ...EMPTY, loading: sessionStatus === 'loading' });
      return;
    }

    const controller = new AbortController();
    setResult((prev) => ({ ...prev, loading: true, error: null }));

    const token = getToken();
    Promise.all([
      fetchHistory(token, window, metrics, controller.signal),
      fetchSummary(token, window, metrics, controller.signal),
    ])
      .then(([points, summary]) => {
        cache.set(key, { at: Date.now(), points, summary });
        setResult({ points, summary, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          points: [],
          summary: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Istoricul nu a putut fi încărcat.',
        });
      });

    return () => controller.abort();
    // `key` rezumă fereastra și metricile; obiectele se recreează la fiecare
    // randare, deci ele n-ar putea fi dependențe stabile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, sessionStatus, tokenVersion, getToken]);

  return result;
}
